import { useCancelImport, useImportJob, useRelaunchImport } from '@/lib/api/imports';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ImportMappingEditor } from '@/features/catalogs/ImportMappingEditor';

interface ImportJobDetailProps {
  catalogId: string;
  jobId: string;
}

/** Progress view, mapping/preview step, and final report for one import job. */
export function ImportJobDetail({ catalogId, jobId }: ImportJobDetailProps) {
  const job = useImportJob(jobId);
  const cancel = useCancelImport(catalogId, jobId);
  const relaunch = useRelaunchImport(catalogId, jobId);

  if (job.isLoading || !job.data) {
    return <Spinner label="Loading import job" />;
  }

  const data = job.data;
  const isRunning = data.status === 'parsing' || data.status === 'applying';
  const isTerminal = data.status === 'done' || data.status === 'failed' || data.status === 'canceled';

  return (
    <div
      className="mt-2 space-y-3 rounded-md border border-gray-200 p-3"
      data-testid="import-job-detail"
    >
      {isRunning && (
        <div data-testid="import-progress">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full bg-brand-600 transition-all"
              style={{ width: `${Math.round((data.progress ?? 0) * 100)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {data.status === 'parsing'
              ? 'Parsing...'
              : `Applying: ${Math.round((data.progress ?? 0) * 100)}%`}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
            data-testid="cancel-import-button"
          >
            Cancel
          </Button>
        </div>
      )}

      {data.status === 'awaiting_mapping' && (
        <ImportMappingEditor catalogId={catalogId} jobId={jobId} />
      )}

      {isTerminal && (
        <div data-testid="import-report">
          <p className="text-sm font-medium text-gray-900">Status: {data.status}</p>
          {data.status === 'done' && (
            <ul className="mt-1 space-y-0.5 text-sm text-gray-700">
              <li>Created: {data.created_count}</li>
              <li>Updated: {data.updated_count}</li>
              <li>Unchanged: {data.unchanged_count}</li>
              <li>Failed: {data.failed_count}</li>
            </ul>
          )}
          {data.error && <p className="mt-1 text-sm text-red-700">{data.error}</p>}
          {data.row_errors.length > 0 && (
            <ul className="mt-2 max-h-40 space-y-0.5 overflow-auto text-xs text-red-700">
              {data.row_errors.map((e) => (
                <li key={e.row_number}>
                  Row {e.row_number}: {e.reason}
                </li>
              ))}
            </ul>
          )}
          {data.status === 'failed' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => relaunch.mutate()}
              disabled={relaunch.isPending}
              className="mt-2"
              data-testid="relaunch-import-button"
            >
              Relaunch
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
